import { useMemo } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import { RootState } from "../../state/store";
import { useAppDispatch } from "../../state/hooks";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import { DateSelectCrumb } from "../Breadcrumbs/DateSelectCrumb";
import { ShiftSelectCrumb } from "../Breadcrumbs/Shift/ShiftSelectCrumb";
import { ShiftLockIcon } from "../Breadcrumbs/Shift/ShiftLockIcon";
import useShiftTimes from "../Hooks/useShiftTimes";

const greetingFor = (hour: number) => {
  if (hour < 5) return "Good evening";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const firstNameFrom = (fullName?: string | null, email?: string | null) => {
  if (fullName && fullName.trim()) return fullName.trim().split(/\s+/)[0];
  if (email) return email.split("@")[0];
  return "there";
};

export const DashboardHeader: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedDate, selectedShift } = useShiftTimes();
  const user = useSelector((s: RootState) => s.authReducer.user);

  const { greeting, firstName, todayLabel } = useMemo(() => {
    const now = DateTime.local();
    return {
      greeting: greetingFor(now.hour),
      firstName: firstNameFrom(user?.name, user?.email),
      todayLabel: now.toFormat("cccc, d LLL yyyy"),
    };
  }, [user?.name, user?.email]);

  return (
    <div className="w-full bg-theme-surface-2 border-b border-theme-border shadow-sm shadow-theme-shadow">
      <div className="mx-auto w-full max-w-[2180px] px-4 md:px-8 py-3 md:py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-base md:text-lg font-semibold text-theme-text leading-tight">
            {greeting}, {firstName}
          </span>
          <span className="text-xs md:text-sm text-theme-text-muted">{todayLabel}</span>
        </div>
        <div className="flex items-center gap-2 md:gap-3 home-crumbs">
          <div className="relative inline-block date-crumb">
            <DateSelectCrumb
              selectedDate={selectedDate}
              setSelectedDate={(date) => dispatch(setSelectedDate(date.toISODate()!))}
            />
          </div>
          <div className="relative inline-flex items-center justify-center shift-crumb">
            <ShiftSelectCrumb
              selectedShift={selectedShift}
              setSelectedShift={(shift) => dispatch(setSelectedShift(shift))}
            />
            <ShiftLockIcon selectedDate={selectedDate} selectedShift={selectedShift} />
          </div>
        </div>
      </div>
    </div>
  );
};
